import { memo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useApolloClient, useMutation } from "util/graphql";
import { encodeSearchParams } from "util/location";
import SelectSignersModal from "common/signer/select_signers_modal";
import TermsOfServiceModal from "common/modals/terms_of_service";
import BundleUnavailableModal from "common/modals/bundle_unavailable_modal";
import BundleNotFoundModal from "common/document_bundle/bundle_not_found_modal";
import { getParticipantGroups } from "util/document_bundle";
import { isHybridTransactionType } from "common/mortgage/transactions/utils";
import { useIsMobileWebEnabled } from "util/mobile_web";
import SelectMobileWebOrAppBlock from "common/signer/select_mobile_web_or_app_block";
import { onlyRequiresEsign } from "util/completion_requirements/completion_requirements_text";
import {
  bundlePrepareRoute,
  useNavigateToBundleViewRoute,
  customerDetailsRoute,
  bundleSignRoute,
  SIGN_PATH,
} from "util/routes";
import {
  esignRequiringAuthenticationWithPersona,
  kbaRequired,
  meetingRequired,
} from "common/signer/utils";
import { useLazyQuery } from "util/graphql/query";
import { useFeatureFlag } from "common/feature_gating";
import { StepType, RequiredFeature } from "graphql_globals";
import { SEGMENT_EVENTS } from "constants/analytics";
import { segmentTrack } from "util/segment";

import InitiateSigningQuery, { type InitiateSigning_viewer as Viewer } from "./index_query.graphql";
import SignerStepsQuery from "./signer_steps_query.graphql";
import SignParticipantTOSMutation from "./sign_participant_tos_mutation.graphql";
import CreateSignerIdentitiesMutation from "./create_signer_identities_mutation.graphql";
import type {
  InitiateSigningDocBundle_participants as Participant,
  InitiateSigningDocBundle as DocumentBundle,
} from "./index_docbundle_fragment.graphql";
import { getCurrentStep, useMapStepToRoute } from "../signer_steps_v2/util";

export const SIGNER_IDS_SESSION_KEY = "signer-identity-ids";

type Modal =
  | "select-signers"
  | "accept-tos"
  | "bundle-unavailable"
  | "bundle-not-found"
  | "mweb-block";
type RenderProps = Omit<ReturnType<typeof getParticipantGroups>, "participantGroup"> & {
  initiateSigningForBundle: () => void;
  loading: boolean;
};
type Props = {
  viewer: Viewer;
  documentBundle: DocumentBundle;
  children: (renderProps: RenderProps) => ReactNode;
};

const getParticipantWhoStillNeedsToAcceptTos = (
  participants: Props["documentBundle"]["participants"],
  userIds: string[],
) => {
  const participantsWhoStillNeedsToAcceptTos = participants?.filter((participant) => {
    return participant && userIds.includes(participant.userId) && !participant.tosSigned;
  });

  return participantsWhoStillNeedsToAcceptTos?.[0] || null;
};

function InitiateSigningWrapper(props: Props) {
  const {
    documentBundle: { id: documentBundleId },
  } = props;

  const [selectedOtherParticipantGroupOwners, setSelectOtherParticipantGroupOwners] = useState<
    string[]
  >([]);
  const [openModal, setOpenModal] = useState<Modal | null>(null);
  const [participantAcceptingTos, setParticipantAcceptingTos] = useState<Participant | null>(null);
  const [outstandingAction, setOutstandingAction] = useState(false);
  const navigate = useNavigate();
  const navigateToBundleView = useNavigateToBundleViewRoute();
  const location = useLocation();
  const client = useApolloClient();
  const isMobileWebEnabled = useIsMobileWebEnabled();
  const signerStepsV2Enabled = useFeatureFlag("signerStepsV2Flow");
  const mapStepToRoute = useMapStepToRoute();

  const [initiateSigning] = useLazyQuery(InitiateSigningQuery);

  function checkIfBundleAvailableForSigning() {
    setOutstandingAction(true);
    return initiateSigning({
      variables: { documentBundleId },
    })
      .then(({ data }) => {
        if (data?.documentBundle?.__typename !== "DocumentBundle") {
          throw new Error("Initiate signing query failed to return data");
        }
        return data.documentBundle.availableForSigning;
      })
      .finally(() => {
        setOutstandingAction(false);
      });
  }

  const signParticipantTOSMutateFn = useMutation(SignParticipantTOSMutation);
  const acceptTermsOfService = (participant: Pick<Participant, "userId">) =>
    signParticipantTOSMutateFn({
      variables: {
        input: {
          userId: participant.userId,
          documentBundleId,
        },
      },
      // prevent the document bundle from being re-fetched by parent components or page will
      // refresh before all participants can sign
      onQueryUpdated: () => false,
    });

  const createSignerIdentitiesMutateFn = useMutation(CreateSignerIdentitiesMutation);
  async function createSignerIdentities(userIds: string[]) {
    const { data } = await createSignerIdentitiesMutateFn({
      variables: {
        input: {
          userIds,
          documentBundleId,
        },
      },
    });

    const signerIdentities = data?.createSignerIdentities?.signerIdentities;
    const { data: viewerData } = await client.query({
      query: SignerStepsQuery,
      variables: { documentBundleId, signerIdentityIds: signerIdentities?.map((s) => s.id) },
    });

    return {
      signerIdentities: data?.createSignerIdentities?.signerIdentities,
      signerSteps: viewerData.viewer.signerSteps,
      signerStepsV2: viewerData.viewer.signerStepsV2,
    };
  }

  async function handleInitiateSigningForBundle() {
    const {
      viewer: { user },
      documentBundle: {
        participants,
        organizationTransaction,
        completionRequirements,
        isMortgage,
        requiredFeatures,
      },
    } = props;
    const { hasOptionalSigners, hasMultipleRequiredSigners } = getParticipantGroups(
      participants,
      user!,
      true,
      requiredFeatures?.includes(RequiredFeature.SIGNING_ORDER),
    );

    const requiresNsaMeeting = organizationTransaction?.requiresNsaMeeting ?? true;
    const docVisibilityEnabled = organizationTransaction?.docVisibilityEnabled;
    const isOnlyEsign = onlyRequiresEsign(completionRequirements);

    let availableForSigning;
    try {
      availableForSigning = await checkIfBundleAvailableForSigning();
    } catch (_err) {
      setOpenModal("bundle-not-found");
      return;
    }

    // Not available for signing yet; redirect to document bundle view to display
    // busy state
    if (!availableForSigning) {
      const skipBundleUnavailable =
        !isMortgage &&
        (location.pathname.endsWith("/sign") || location.pathname.endsWith("/prepare"));
      if (skipBundleUnavailable) {
        setOpenModal(null);
        return navigateToBundleView({ bundleId: documentBundleId });
      }
      setOpenModal("bundle-unavailable");
      return;
    }

    // if on mobile and the transaction does not support mobile web, present
    // the download app block.
    if (!isMobileWebEnabled) {
      setOpenModal("mweb-block");
      return;
    }

    // Available for signing; decide whether we need to open the select signers
    // modal.
    // Skip modal if:
    // - Nsa meeting not required
    // - is only an esign
    // - Doc visibility is enabled
    // - both required and optional signers are present
    // - both required and optional signers are missing
    if (
      docVisibilityEnabled ||
      isOnlyEsign ||
      (!hasOptionalSigners && !hasMultipleRequiredSigners) ||
      !requiresNsaMeeting
    ) {
      if (user) {
        createSignerIdentitiesForParticipantsAndRedirect([user.id]);
      }
    } else {
      setOpenModal("select-signers");
    }
  }

  function handleContinueSelectModalClicked() {
    findParticipantsMissingTosOrCreateSignerIdentitiesThenRedirect(
      (props.documentBundle.participants || []) as Participant[],
    );
  }

  function handleContinueAcceptTosModalClicked(participant: Pick<Participant, "userId">) {
    setOutstandingAction(true);

    acceptTermsOfService(participant)
      .then(({ data }) => {
        if (!data?.signParticipantTos?.document_bundle) {
          throw new Error("Failed to retrieve documentBundle after accepting terms of service");
        }
        findParticipantsMissingTosOrCreateSignerIdentitiesThenRedirect(
          (data.signParticipantTos.document_bundle.participants || []) as Participant[],
        );
      })
      .finally(() => {
        setOutstandingAction(false);
      });
  }

  function closeModals() {
    setOpenModal(null);
    navigate(SIGN_PATH);
  }

  function findParticipantsMissingTosOrCreateSignerIdentitiesThenRedirect(
    participants: Participant[],
  ) {
    const {
      viewer: { user },
      documentBundle: { isMortgage },
    } = props;

    const { participantGroups, participantGroup } = getParticipantGroups(participants, user!, true);

    const selectedParticipantGroups = (participantGroup || []).concat(
      selectedOtherParticipantGroupOwners.flatMap((owner) => participantGroups[owner]),
    );

    const selectedParticipants = selectedParticipantGroups.map((participant) => participant.userId);

    const participant = getParticipantWhoStillNeedsToAcceptTos(participants, selectedParticipants);

    if (!participant || !isMortgage) {
      createSignerIdentitiesForParticipantsAndRedirect(selectedParticipants);
    } else {
      setParticipantAcceptingTos(participant);
      setOpenModal("accept-tos");
    }
  }

  function createSignerIdentitiesForParticipantsAndRedirect(userIds: string[]) {
    const {
      documentBundle,
      documentBundle: { id, includesEnote, organizationTransaction, isMortgage, signAhead },
    } = props;

    const isHybrid =
      organizationTransaction?.transactionType &&
      isHybridTransactionType(organizationTransaction.transactionType);

    const redirectToSignAhead = signAhead && isMortgage;

    setOutstandingAction(true);

    createSignerIdentities(userIds).then(({ signerIdentities, signerSteps, signerStepsV2 }) => {
      const signerIdentityIds = signerIdentities?.map((signerIdentity) => signerIdentity.id) || [];
      const requiresKba = kbaRequired(signerSteps);
      // currently, we do not consider hybrids to be eSigns :'(, need to remove isHybrid once we consolidate with eSign
      const isEsign = !meetingRequired(signerSteps) && !isHybrid;
      const esignRequiringVerification =
        isEsign && (requiresKba || esignRequiringAuthenticationWithPersona(signerSteps));
      const redirectToVerification = (isHybrid && includesEnote) || esignRequiringVerification;
      const currentStep = getCurrentStep(signerStepsV2);
      let expectedNextStepType;

      let pathname;
      if (redirectToVerification) {
        pathname = customerDetailsRoute(id);
        expectedNextStepType = StepType.PERSONAL_DETAILS;
      } else if (redirectToSignAhead) {
        pathname = bundleSignRoute(id);
        expectedNextStepType = StepType.DOCUMENT;
      } else {
        pathname = bundlePrepareRoute(id);
        expectedNextStepType = StepType.DOCUMENT;
      }

      if (currentStep?.stepType !== expectedNextStepType) {
        segmentTrack(SEGMENT_EVENTS.NEXT_STEP_MISMATCH, {
          current_step: "initialization",
          actual_step: currentStep,
          expected_step: expectedNextStepType,
          bundle_id: id,
        });
      }

      const search = encodeSearchParams(
        new URLSearchParams([
          ["step", redirectToVerification ? "quiz_setup" : "prefill"],
          ["currentSignerIdentityId", signerIdentityIds[0]],
          ...signerIdentityIds.map((si) => ["signerIdentityIds", si]),
        ]),
      );
      window.sessionStorage.setItem(SIGNER_IDS_SESSION_KEY, signerIdentityIds.join(","));
      setOutstandingAction(false);
      setOpenModal(null);

      if (signerStepsV2Enabled) {
        pathname = mapStepToRoute({
          step: currentStep,
          documentBundle,
        });
      }
      // kick start signer steps by setting the first step as the current step
      navigate(`${pathname}?${search}`, {
        state: { currentStepId: currentStep?.id },
        replace: true,
      });
    });
  }

  function handleParticipantSelectToggled(participantId: string) {
    setSelectOtherParticipantGroupOwners(
      selectedOtherParticipantGroupOwners.includes(participantId)
        ? selectedOtherParticipantGroupOwners.filter((participant) => participant !== participantId)
        : [...selectedOtherParticipantGroupOwners, participantId],
    );
  }

  function handleViewDocumentsClicked() {
    const {
      documentBundle: { id },
    } = props;

    if (openModal === "select-signers") {
      return navigateToBundleView({ bundleId: id, viewOnly: true });
    }
    navigateToBundleView({ bundleId: id });
    setOpenModal(null);
  }

  const {
    viewer,
    documentBundle,
    documentBundle: { participants, organizationTransaction, requiredFeatures },
    children,
  } = props;

  const {
    participantGroups,
    participantGroupOwners,
    hasOptionalSigners,
    hasMultipleRequiredSigners,
    participantGroup,
  } = getParticipantGroups(
    participants,
    viewer.user!,
    true,
    requiredFeatures?.includes(RequiredFeature.SIGNING_ORDER),
  );

  return (
    <>
      {openModal === "bundle-not-found" ? (
        <BundleNotFoundModal />
      ) : openModal === "bundle-unavailable" ? (
        <BundleUnavailableModal
          firstName={viewer.user?.firstName || ""}
          onContinue={handleViewDocumentsClicked}
          documentBundle={documentBundle}
        />
      ) : openModal === "mweb-block" ? (
        <SelectMobileWebOrAppBlock organizationTransaction={organizationTransaction} />
      ) : openModal === "select-signers" ? (
        <SelectSignersModal
          documentBundle={documentBundle}
          participantGroups={participantGroups}
          participantGroupOwners={participantGroupOwners}
          selectedOtherParticipantGroupOwners={selectedOtherParticipantGroupOwners}
          onParticipantSelectToggled={handleParticipantSelectToggled}
          onContinue={handleContinueSelectModalClicked}
          onCancel={closeModals}
          onViewDocuments={handleViewDocumentsClicked}
          loading={outstandingAction}
          hasMultipleRequiredSigners={hasMultipleRequiredSigners}
          participantGroup={participantGroup || []}
        />
      ) : openModal === "accept-tos" && participantAcceptingTos ? (
        <TermsOfServiceModal
          onAccept={handleContinueAcceptTosModalClicked}
          onCancel={closeModals}
          user={participantAcceptingTos}
          loading={outstandingAction}
          showForm
        />
      ) : null}
      {children({
        initiateSigningForBundle: handleInitiateSigningForBundle,
        loading: outstandingAction,
        participantGroups,
        participantGroupOwners,
        hasOptionalSigners,
        hasMultipleRequiredSigners,
      })}
    </>
  );
}

export default memo(InitiateSigningWrapper);
